import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { salesOrderService } from '../../../services/salesOrderService';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import TableWithMonthDayGroups from '../../../components/Common/TableWithMonthDayGroups';
import VentasCloseShiftModal from '../../../components/Ventas/VentasCloseShiftModal';
import VentasCreateShiftModal from '../../../components/Ventas/VentasCreateShiftModal';
import SalesOrderPdfButton from '../../../components/Ventas/SalesOrderPdfButton';
import { closeShift, reopenShift } from '../../../services/salesShiftClosureService';
import { formatDateLocaleLima, formatDateToLima } from '../../../utils/dateUtils';
import MutateGate from '../../../components/Common/MutateGate';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { POS_CONFIRMED_STATE } from '../../../utils/ventasPosNavigation';
import { getVentasDocumentTypeLabel } from '../../../utils/ventasSalesConstants';

const VentasOrdersIndex = () => {
  const canMutate = useCanMutate();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [orderToToggle, setOrderToToggle] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [closedShiftDates, setClosedShiftDates] = useState([]);
  const [lastCorrelativeId, setLastCorrelativeId] = useState(null);
  const [closeShiftDay, setCloseShiftDay] = useState(null);
  const [closeShiftLoading, setCloseShiftLoading] = useState(false);
  const [createShiftOpen, setCreateShiftOpen] = useState(false);
  const [reopenShiftTarget, setReopenShiftTarget] = useState(null);
  const [reopenShiftLoading, setReopenShiftLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedInitialData = useRef(false);
  const previousFilters = useRef({ statusFilter: 'all', searchTerm: '', dateFrom: '', dateTo: '' });
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (location.state?.orderSavedMessage) {
      setToast({ isOpen: true, message: location.state.orderSavedMessage, type: 'success' });
      if (selectedPointOfSale?.id) {
        loadOrders();
      }
      window.history.replaceState(
        { ...window.history.state, usr: { ...location.state, orderSavedMessage: undefined } },
        '',
      );
    }
  }, [location.state?.orderSavedMessage, selectedPointOfSale?.id]);

  useEffect(() => {
    if (!selectedPointOfSale?.id) return;
    hasLoadedInitialData.current = true;
    previousFilters.current = { statusFilter, searchTerm, dateFrom, dateTo };
    loadOrders();
  }, [selectedPointOfSale?.id]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousFilters.current = { statusFilter, searchTerm, dateFrom, dateTo };
      return;
    }
    if (hasLoadedInitialData.current && selectedPointOfSale?.id) {
      const filtersChanged =
        previousFilters.current.statusFilter !== statusFilter ||
        previousFilters.current.searchTerm !== searchTerm ||
        previousFilters.current.dateFrom !== dateFrom ||
        previousFilters.current.dateTo !== dateTo;
      if (filtersChanged) {
        previousFilters.current = { statusFilter, searchTerm, dateFrom, dateTo };
        loadOrders();
      }
    }
  }, [statusFilter, searchTerm, dateFrom, dateTo, selectedPointOfSale?.id]);

  const loadOrders = async () => {
    if (!selectedPointOfSale?.id) return;
    setLoadingOrders(true);
    try {
      const params = { point_of_sale_id: selectedPointOfSale.id };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await salesOrderService.getOrders(params);
      if (response.success && response.data) {
        setOrders(response.data);
        setClosedShiftDates(response.meta?.closed_shift_dates || []);
        setLastCorrelativeId(response.meta?.last_correlative_id ?? null);
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar ventas', type: 'error' });
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleToggleStatusClick = (order) => {
    setOrderToToggle(order);
    setIsToggleModalOpen(true);
  };

  const handleToggleStatusConfirm = async () => {
    if (!orderToToggle) return;
    setLoading(true);
    try {
      const response = await salesOrderService.toggleStatus(orderToToggle.id);
      await loadOrders();
      setIsToggleModalOpen(false);
      setOrderToToggle(null);
      setToast({
        isOpen: true,
        message: response.message || 'Estado actualizado',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cambiar estado',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    setLoading(true);
    try {
      const response = await salesOrderService.deleteOrder(orderToDelete.id);
      await loadOrders();
      setIsDeleteModalOpen(false);
      setOrderToDelete(null);
      setToast({
        isOpen: true,
        message: response.message || 'Venta eliminada',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la venta',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const totalOrders = orders.length;
  const issuedOrders = orders.filter((o) => o.status === 'issued').length;
  const cancelledOrders = orders.filter((o) => o.status === 'cancelled').length;
  // Monto solo de emitidas (las anuladas no suman, igual que el dashboard).
  const totalAmount = orders
    .filter((o) => o.status === 'issued')
    .reduce((sum, o) => sum + (parseFloat(o.total_amount) || 0), 0);

  const formatCurrency = (amount) =>
    `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const sumDayAmount = (dayGroup) =>
    dayGroup.items
      .filter((order) => order.status === 'issued')
      .reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0);

  const isDayClosed = (dayGroup) => {
    const dateKey = dayGroup.date || dayGroup.key;
    return closedShiftDates.includes(dateKey);
  };

  const todayKey = formatDateToLima(new Date());
  const isTodayClosed = closedShiftDates.includes(todayKey);

  const getOrderGuides = (order) => {
    const guides = [];
    const addGuide = (guide, kind) => {
      if (!guide?.id || guides.some((g) => g.id === guide.id)) return;
      guides.push({
        id: guide.id,
        code: guide.code,
        kind,
      });
    };

    (order.exit_guides || []).forEach((guide) => addGuide(guide, 'documento'));
    (order.referenced_exit_guides || []).forEach((guide) => addGuide(guide, 'referencia'));

    return guides;
  };

  const renderGuideLinks = (guides) => {
    if (!guides.length) {
      return <span className="text-gray-400">—</span>;
    }
    return (
      <div className="flex flex-col gap-1">
        {guides.map((guide) => (
          <Link
            key={guide.id}
            to={`/exit-guides/${guide.id}/edit`}
            className="text-sm font-mono text-teal-600 hover:text-teal-900"
            title="Guía de salida del comprobante"
          >
            {guide.code || `GS-${guide.id}`}
          </Link>
        ))}
      </div>
    );
  };

  const handleCloseShiftClick = (dayGroup) => {
    if (isDayClosed(dayGroup)) return;
    setCloseShiftDay(dayGroup);
  };

  const handleCloseShiftConfirm = async ({ shift_date, warehouse_id, exit_time, notes }) => {
    if (!selectedPointOfSale?.id) return;
    setCloseShiftLoading(true);
    try {
      const response = await closeShift({
        point_of_sale_id: selectedPointOfSale.id,
        shift_date,
        warehouse_id,
        exit_time,
        notes,
      });
      if (response.success) {
        setToast({ isOpen: true, message: response.message || 'Turno cerrado', type: 'success' });
        setCloseShiftDay(null);
        await loadOrders();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo cerrar el turno', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cerrar el turno',
        type: 'error',
      });
    } finally {
      setCloseShiftLoading(false);
    }
  };

  const handleReopenShiftClick = (dateKey, label) => {
    if (!selectedPointOfSale?.id || !dateKey) {
      setToast({ isOpen: true, message: 'No se pudo identificar el turno a reabrir', type: 'error' });
      return;
    }
    setReopenShiftTarget({ dateKey, label });
  };

  const handleCreateShiftConfirm = ({ shift_date }) => {
    if (!shift_date) return;
    if (closedShiftDates.includes(shift_date)) {
      setToast({
        isOpen: true,
        message: 'Ese día ya tiene el turno cerrado. No se pueden crear documentos en una fecha cerrada.',
        type: 'error',
      });
      return;
    }
    setCreateShiftOpen(false);
    navigate('/ventas/orders/create', {
      state: { ...POS_CONFIRMED_STATE, issue_date: shift_date },
    });
  };

  const handleReopenShiftConfirm = async () => {
    if (!reopenShiftTarget?.dateKey || !selectedPointOfSale?.id) return;
    setReopenShiftLoading(true);
    try {
      const response = await reopenShift({
        point_of_sale_id: selectedPointOfSale.id,
        shift_date: reopenShiftTarget.dateKey,
      });
      if (response.success) {
        setToast({ isOpen: true, message: response.message || 'Turno reabierto', type: 'success' });
        setReopenShiftTarget(null);
        await loadOrders();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo reabrir el turno', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al reabrir el turno',
        type: 'error',
      });
    } finally {
      setReopenShiftLoading(false);
    }
  };

  const handlePdfSuccess = (order) => {
    setToast({
      isOpen: true,
      message: `PDF de ${order.code} descargado`,
      type: 'success',
    });
  };

  const handlePdfError = (message) => {
    setToast({
      isOpen: true,
      message: message || 'Error al generar el PDF',
      type: 'error',
    });
  };

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Ventas</h1>
                <p className="text-gray-600 mt-1">
                  Ventas del día agrupadas por mes. Al finalizar el turno use ejecutar salidas y cerrar el día.
                </p>
              </div>
              <MutateGate>
                <div className="flex items-center gap-2">
                  {isTodayClosed ? (
                    <span className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-md inline-block">
                      Turno de hoy cerrado
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setCreateShiftOpen(true)}
                    className="px-4 py-2 text-sm font-medium rounded-md bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                  >
                    Crear documentos
                  </button>
                  <Link
                    to="/ventas/orders/bulk-import"
                    className="px-4 py-2 text-sm font-medium rounded-md border border-teal-600 text-teal-700 hover:bg-teal-50 transition-colors"
                  >
                    Importar masiva
                  </Link>
                </div>
              </MutateGate>
            </div>

            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/ventas/clients" className="hover:text-teal-600">Ventas</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Ventas</span>
            </nav>

            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los estados</option>
                  <option value="issued">Emitidas</option>
                  <option value="cancelled">Canceladas</option>
                </select>
                <input
                  type="text"
                  placeholder="Buscar por código o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Total</div>
                  <div className="text-2xl font-bold text-gray-800">{totalOrders}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Emitidas</div>
                  <div className="text-2xl font-bold text-teal-600">{issuedOrders}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Canceladas</div>
                  <div className="text-2xl font-bold text-gray-500">{cancelledOrders}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Monto total (emitidas)</div>
                  <div className="text-2xl font-bold text-blue-600">{formatCurrency(totalAmount)}</div>
                </div>
              </div>
            </div>
          </div>

          {loadingOrders ? (
            <div className="text-center py-12 text-gray-500">Cargando ventas...</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron ventas</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <TableWithMonthDayGroups
                data={orders}
                getDateForGrouping={(o) => o.issue_date}
                defaultCollapsed
                columnCount={12}
                emptyMessage="No se encontraron ventas"
                renderDaySummary={(dayGroup) => (
                  <span>{`· Total del día: ${formatCurrency(sumDayAmount(dayGroup))}`}</span>
                )}
                renderDayActions={(dayGroup) => (
                  <MutateGate>
                    {isDayClosed(dayGroup) ? (
                      <button
                        type="button"
                        onClick={() => handleReopenShiftClick(dayGroup.date || dayGroup.key, dayGroup.label)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-orange-500 text-orange-600 bg-white hover:bg-orange-50 whitespace-nowrap"
                      >
                        Abrir turno
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleCloseShiftClick(dayGroup)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-teal-600 text-teal-700 bg-white hover:bg-teal-50 whitespace-nowrap"
                      >
                        Cerrar turno
                      </button>
                    )}
                  </MutateGate>
                )}
                headerRow={
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ítems</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base imponible</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IGV</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comprobante</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guía</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                }
                renderRow={(order) => {
                  const statusLabel = order.status === 'cancelled' ? 'Cancelada' : 'Emitida';
                  const statusClass =
                    order.status === 'cancelled'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800';
                  const orderDateKey = formatDateToLima(order.issue_date);
                  const dayShiftClosed = closedShiftDates.includes(orderDateKey);
                  const canEditOrder =
                    canMutate && order.status !== 'cancelled' && !dayShiftClosed;
                  const canMutateOrderDay = canMutate && !dayShiftClosed;
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link
                          to={`/ventas/orders/${order.id}/edit`}
                          state={POS_CONFIRMED_STATE}
                          className="text-sm font-mono text-teal-600 hover:text-teal-900"
                        >
                          {order.code}
                        </Link>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{order.client?.name || 'N/A'}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateLocaleLima(order.issue_date)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.items?.length || 0} ítems
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatCurrency(order.base_imponible)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatCurrency(order.igv_amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(order.total_amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div>{getVentasDocumentTypeLabel(order.document_type)}</div>
                        {order.series && order.number ? (
                          <div className="text-xs font-mono text-gray-500 mt-0.5">
                            {order.series}-{order.number}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {[
                          ...(order.sales_quotes || []).map((q) => q.code),
                          ...(order.sales_continuous_services || []).map((s) => s.code),
                        ].join(', ') || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {renderGuideLinks(getOrderGuides(order))}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-medium">
                        <div className="flex flex-wrap items-center gap-2">
                          <SalesOrderPdfButton
                            orderId={order.id}
                            onSuccess={handlePdfSuccess}
                            onError={handlePdfError}
                          />
                          <Link
                            to={`/ventas/orders/${order.id}/edit`}
                            state={POS_CONFIRMED_STATE}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            {canEditOrder ? 'Editar' : 'Ver'}
                          </Link>
                          {canMutateOrderDay ? (
                            <MutateGate>
                              <button
                                type="button"
                                onClick={() => handleToggleStatusClick(order)}
                                className={
                                  order.status === 'issued'
                                    ? 'text-orange-600 hover:text-orange-900'
                                    : 'text-green-600 hover:text-green-900'
                                }
                              >
                                {order.status === 'issued' ? 'Anular' : 'Reactivar'}
                              </button>
                              {order.status === 'cancelled' && lastCorrelativeId === order.id ? (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(order)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Eliminar
                                </button>
                              ) : null}
                            </MutateGate>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>

      <VentasCreateShiftModal
        isOpen={createShiftOpen}
        pointOfSale={selectedPointOfSale}
        closedShiftDates={closedShiftDates}
        onClose={() => setCreateShiftOpen(false)}
        onConfirm={handleCreateShiftConfirm}
      />

      <VentasCloseShiftModal
        isOpen={Boolean(closeShiftDay)}
        dayGroup={closeShiftDay}
        pointOfSale={selectedPointOfSale}
        loading={closeShiftLoading}
        onClose={() => !closeShiftLoading && setCloseShiftDay(null)}
        onConfirm={handleCloseShiftConfirm}
      />

      <ConfirmModal
        isOpen={Boolean(reopenShiftTarget)}
        onClose={() => !reopenShiftLoading && setReopenShiftTarget(null)}
        onConfirm={handleReopenShiftConfirm}
        title="Abrir turno"
        message={
          reopenShiftTarget
            ? `¿Abrir el turno de ${reopenShiftTarget.label || reopenShiftTarget.dateKey}? Podrá registrar y modificar ventas de esa fecha. Las guías de salida ya generadas se mantienen; al volver a cerrar solo se generarán guías para las ventas nuevas.`
            : ''
        }
        confirmText="Sí, abrir turno"
        cancelText="No"
        isLoading={reopenShiftLoading}
      />

      <ConfirmModal
        isOpen={isToggleModalOpen}
        onClose={() => {
          setIsToggleModalOpen(false);
          setOrderToToggle(null);
        }}
        onConfirm={handleToggleStatusConfirm}
        title={orderToToggle?.status === 'issued' ? 'Anular venta' : 'Reactivar venta'}
        message={
          orderToToggle
            ? orderToToggle.status === 'issued'
              ? `¿Anular la venta "${orderToToggle.code}"? Las guías de salida asociadas quedarán pendientes y se revertirá el stock.`
              : `¿Reactivar la venta "${orderToToggle.code}"? Al cerrar el turno se generará de nuevo la guía de salida si corresponde.`
            : ''
        }
        confirmText="Sí"
        cancelText="No"
        isLoading={loading}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setOrderToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar venta"
        message={
          orderToDelete
            ? `¿Eliminar definitivamente la venta "${orderToDelete.code}"? Esta acción libera el correlativo para usarlo de nuevo.`
            : ''
        }
        confirmText="Sí, eliminar"
        cancelText="No"
        isLoading={loading}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default VentasOrdersIndex;
